// src/modules/app-views/products/pages/Init/modals/ProductsParametersModal/tabs/TagFormModal.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Flex, Space, Form, Input, ColorPicker } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const RANDOM_COLORS = [
  '#1677ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1',
  '#13c2c2', '#eb2f96', '#fa8c16', '#2f54eb', '#a0d911',
  '#fadb14', '#f5222d', '#1890ff', '#9254de', '#36cfc9',
];

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const TagFormModal = ({ open, onCancel, onSave, initialData, loading, mousePosition }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const [form] = Form.useForm();
  const inputRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const isEditing = !!initialData;
  const title = isEditing ? translateX('edit_tag') : translateX('new_tag');

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      if (initialData) {
        form.setFieldsValue({
          label: initialData.label || '',
          color: initialData.color || getRandomColor(),
        });
      } else {
        form.setFieldsValue({
          label: '',
          color: getRandomColor(),
        });
      }

      // Auto-focus on label field
      setTimeout(() => {
        inputRef.current?.focus();
      }, 150);
    }
  }, [open, initialData, form]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      // Normalize color value (AntD ColorPicker may return Color object)
      const colorValue = typeof values.color === 'string'
        ? values.color
        : values.color?.toHexString?.() || values.color?.metaColor?.toHexString?.() || '#1677ff';

      onSave({
        label: (values.label || '').trim(),
        color: colorValue,
      });
    } catch (error) {
      // Validation failed
    }
  }, [form, onSave]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    onCancel();
  }, [form, onCancel]);

  const handleAfterClose = useCallback(() => {
    form.resetFields();
  }, [form]);

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleCancel}
      afterClose={handleAfterClose}
      destroyOnHidden
      centered
      mousePosition={mousePosition}
      getContainer={document.body}
      width={400}
      footer={
        <Flex justify="end" style={{ paddingTop: 8 }}>
          <Space>
            <Button onClick={handleCancel} disabled={loading} icon={<CloseOutlined />}>
              {translateX('cancel')}
            </Button>
            <Button
              type="primary"
              loading={loading}
              icon={<CheckOutlined />}
              onClick={handleSubmit}
            >
              {translateX('save')}
            </Button>
          </Space>
        </Flex>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onKeyDown={handleKeyDown}
        initialValues={{ label: '', color: '#1677ff' }}
      >
        <Flex gap={12} align="flex-end">
          <Form.Item
            name="color"
            label={translateX('color')}
            style={{ marginBottom: 0 }}
            getValueFromEvent={color => color?.toHexString?.() || color}
          >
            <ColorPicker
              format="hex"
              size="small"
              style={{ width: 40, height: 40 }}
              presets={[{ label: translateX('suggested'), colors: RANDOM_COLORS }]}
              disabled={loading}
            />
          </Form.Item>

          <Form.Item
            name="label"
            label={translateX('name')}
            rules={[{ required: true, message: translateX('required_field') }]}
            style={{ flex: 1, marginBottom: 0 }}
          >
            <Input
              ref={inputRef}
              maxLength={30}
              style={{ height: 40 }}
              placeholder={translateX('tag_name')}
              disabled={loading}
            />
          </Form.Item>
        </Flex>
      </Form>
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default TagFormModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function getRandomColor() {
  return RANDOM_COLORS[Math.floor(Math.random() * RANDOM_COLORS.length)];
}
